import React from 'react';
import { Helmet } from 'react-helmet-async';

// Default site metadata
const defaultMeta = {
  siteName: 'WSafe Training™ - Free OSHA Safety Course (Patent Pending)',
  siteUrl: 'https://wsafetraining.com',
  defaultTitle: 'Free OSHA Safety Training Course | WSafe Training™',
  defaultDescription: 'Get your free OSHA safety certification with our comprehensive 40+ hour online course. Cover workplace hazards, PPE, fire safety, electrical safety, ergonomics, and more. 100% free with certificate.',
  defaultImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200',
  twitterHandle: '@wsafetraining',
  keywords: 'OSHA training, workplace safety, safety certification, free safety course, OSHA certification, workplace hazards, PPE training, fire safety training, electrical safety, ergonomics, safety compliance'
};

// SEO component with Open Graph and Twitter cards
const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  article,
  noindex = false 
}) => {
  const seo = {
    title: title ? `${title} | WSafe Training™` : defaultMeta.defaultTitle,
    description: description || defaultMeta.defaultDescription,
    image: image || defaultMeta.defaultImage,
    url: url || defaultMeta.siteUrl,
  };

  // Structured data for organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "WSafe Training™",
    "url": defaultMeta.siteUrl,
    "logo": `${defaultMeta.siteUrl}/logo.png`,
    "description": "Free OSHA safety training and certification courses (Patent Pending)",
    "sameAs": [
      "https://twitter.com/wsafetraining",
      "https://linkedin.com/company/wsafetraining"
    ]
  };

  // Structured data for course
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "OSHA Safety Training Certification Course",
    "description": seo.description,
    "provider": {
      "@type": "Organization",
      "name": "WSafe Training™",
      "sameAs": defaultMeta.siteUrl
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "category": "Free"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT40H"
    },
    "educationalLevel": "Beginner",
    "about": [
      "Workplace Safety",
      "OSHA Compliance",
      "Occupational Health"
    ],
    "teaches": [
      "OSHA regulations and worker rights",
      "Hazard recognition and risk assessment",
      "Personal protective equipment (PPE)",
      "Fire safety and emergency preparedness",
      "Electrical safety and LOTO procedures",
      "Ergonomics and manual material handling",
      "Fall protection and confined spaces",
      "Safety compliance and recordkeeping"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={defaultMeta.keywords} />
      <link rel="canonical" href={seo.url} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={defaultMeta.siteName} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaultMeta.twitterHandle} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      
      {/* Article specific (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(courseSchema)}
      </script>
    </Helmet>
  );
};

// Module-specific SEO
export const ModuleSEO = ({ moduleId, moduleTitle, moduleDescription }) => {
  const moduleSchema = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "name": `Module ${moduleId}: ${moduleTitle}`,
    "description": moduleDescription,
    "learningResourceType": "Online Course Module",
    "educationalLevel": "Beginner",
    "isPartOf": {
      "@type": "Course",
      "name": "OSHA Safety Training Certification Course"
    },
    "provider": {
      "@type": "Organization",
      "name": "WSafe Training™"
    }
  };

  return (
    <Helmet>
      <title>{`${moduleTitle} | OSHA Training Module ${moduleId} | WSafe Training™`}</title>
      <meta name="description" content={moduleDescription} />
      <meta property="og:title" content={`${moduleTitle} - OSHA Training`} />
      <meta property="og:description" content={moduleDescription} />
      <script type="application/ld+json">
        {JSON.stringify(moduleSchema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema component
export const FAQSchema = ({ faqs }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema component
export const BreadcrumbSchema = ({ items }) => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
